import { useIonRouter } from '@ionic/react';
import { WorkspaceInvitationsApi } from '@up2stay/up2promo-api-client';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  healthOkFgClass,
  healthOkIconSoftClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import type { AxiosError } from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { shellPaths } from '@/core/router/app-paths';

import { AppPageContent } from '@/core/components/app-page-content';
import { useErrorMessage } from '@/core/hooks/use-error-message';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { AppIonPage } from '@/core/components/app-ion-page';

enum InvitationStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

const TITLE_CLASS = 'text-[15.5px] font-bold leading-tight text-foreground';
const BODY_CLASS = 'text-[13.5px] leading-relaxed text-muted-foreground';

export function AcceptInvitationPage() {
  const dict = useDictionary();
  const invitationDict = dict.workspace.invitation;
  const router = useIonRouter();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { refetch } = useWorkspaces();
  const getErrorMessage = useErrorMessage();

  const membershipId = searchParams.get('membershipId') ?? null;
  const redirect = searchParams.get('redirectUrl') ?? null;

  const [status, setStatus] = useState<InvitationStatus>(() => {
    if (!membershipId) {
      return InvitationStatus.ERROR;
    }
    return InvitationStatus.LOADING;
  });
  const [error, setError] = useState<string | null>(() => {
    if (!membershipId) {
      return invitationDict.errors.missingMembershipId;
    }
    return null;
  });
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!membershipId) {
      return;
    }

    const acceptInvitation = async () => {
      try {
        await WorkspaceInvitationsApi.acceptInvitation({
          path: {
            membershipId,
          },
        });

        await refetch();

        setStatus(InvitationStatus.SUCCESS);
        const finalRedirectUrl = redirect
          ? decodeURIComponent(redirect)
          : shellPaths.home;
        setRedirectUrl(finalRedirectUrl);

        setTimeout(() => {
          router.push(finalRedirectUrl, 'root', 'replace');
        }, 2000);
      } catch (err) {
        const error = err as AxiosError<{ errors?: Record<string, string> }>;
        setStatus(InvitationStatus.ERROR);
        const backendErrors = error.response?.data?.errors;
        const firstCode =
          (backendErrors &&
            (Object.values(backendErrors)[0] as string | undefined)) ||
          undefined;

        setError(
          getErrorMessage(firstCode) || invitationDict.errors.acceptFailed,
        );
      }
    };

    void acceptInvitation();
  }, [
    membershipId,
    redirect,
    router,
    refetch,
    getErrorMessage,
    invitationDict,
  ]);

  return (
    <AppIonPage>
      <AppPageContent>
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md overflow-hidden">
            <CardHeader className="gap-2 p-5 sm:p-6">
              <CardTitle className={TITLE_CLASS}>
                {invitationDict.accept.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 px-5 pb-5 sm:px-6 sm:pb-6">
              {status === InvitationStatus.LOADING && (
                <div className="flex flex-col items-center gap-4 py-2">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className={BODY_CLASS}>
                    {invitationDict.accept.processing}
                  </p>
                </div>
              )}

              {status === InvitationStatus.SUCCESS && (
                <div className="flex flex-col items-center gap-4">
                  <div
                    className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-2xl',
                      healthOkIconSoftClass,
                    )}
                  >
                    <CheckCircle2 className="h-6 w-6" aria-hidden />
                  </div>
                  <Alert className={cn(healthOkSurfaceClass)}>
                    <AlertDescription className={healthOkFgClass}>
                      {invitationDict.accept.success}
                    </AlertDescription>
                  </Alert>
                  {redirectUrl && (
                    <p className={BODY_CLASS}>
                      {invitationDict.accept.redirecting}
                    </p>
                  )}
                </div>
              )}

              {status === InvitationStatus.ERROR && (
                <div className="flex flex-col items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                    <XCircle className="h-6 w-6" aria-hidden />
                  </div>
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                  <Button
                    variant="primary"
                    onClick={() =>
                      router.push(shellPaths.home, 'root', 'replace')
                    }
                  >
                    {invitationDict.accept.goToDashboard}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </AppPageContent>
    </AppIonPage>
  );
}
